import { House } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LinkAction } from "../../components/Screen.js";

import { AccountsPanel } from "./AccountsPanel.js";
import { JobsPanel } from "./JobsPanel.js";
import { QueuePanel } from "./QueuePanel.js";
import { RulesPanel } from "./RulesPanel.js";
import { ScopePanel } from "./ScopePanel.js";
import { StatusPanel } from "./StatusPanel.js";

/** Which of the three jobs a curator is doing. */
type Tab = "queue" | "accounts" | "scope" | "rules" | "jobs" | "status";

/**
 * The curator's screen — `/admin`, phase 13e.
 *
 * # The queue is the landing tab, because the queue is the job
 *
 * Status is a thing you go and look at; the queue is work waiting for
 * somebody. A screen that opened on a dashboard would make the actual task the
 * second thing you see.
 *
 * # There is no "you are not an admin" state to render
 *
 * Every admin route answers **404** to anybody who is not one — including
 * anybody signed out — because a status that says *forbidden* tells somebody who
 * guessed the URL that they guessed a real one. So each panel treats a failed
 * load as *nothing here*, in the same words a wrong URL would get, and this page
 * does not ask the session whether you may be here: asking would put the answer
 * on the client, which is the one place it must not be.
 */
/** The tabs, in the order they are shown. The copy is at `admin.tab.<key>`. */
const TABS: readonly Tab[] = ["queue", "accounts", "scope", "rules", "jobs", "status"];

export function AdminPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("queue");

  return (
    <main className="mx-auto flex min-h-full w-full max-w-4xl flex-col gap-5 p-5 pb-16">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">
          {t("app.name")}
        </p>
        <h1 className="mt-1 text-3xl font-semibold">{t("menu.curation")}</h1>
        <p className="mt-1 text-sm text-white/60">{t("admin.lead")}</p>
      </header>

      {/* 🔴 **`flex-wrap`, and it is not cosmetic** — reported from a real phone
          on the day this deployed. Six tabs at `px-4` are 572px of content in a
          390px viewport, so the nav pushed the **whole page** into horizontal
          scroll: two tabs unreachable, and every panel below shifted off the
          left edge. `frontend.md` states the rule this broke — *the page body
          must never scroll horizontally*.

          Wrapping rather than an `overflow-x-auto` strip, deliberately: this is
          a screen somebody visits occasionally, and a tab you can only find by
          swiping a strip that does not look scrollable is a tab that is not
          there. Two rows on a phone costs nothing on a page that is otherwise
          mostly empty. */}
      <nav aria-label={t("admin.sections")} className="flex flex-wrap gap-2">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? "page" : undefined}
            onClick={() => {
              setTab(key);
            }}
            className={
              tab === key
                ? "min-h-12 whitespace-nowrap rounded-lg border border-flash-500 bg-flash-500/15 px-4 font-medium"
                : "min-h-12 whitespace-nowrap rounded-lg border border-white/15 px-4 font-medium text-white/70"
            }
          >
            {t(`admin.tab.${key}`)}
          </button>
        ))}
      </nav>

      {tab === "queue" && <QueuePanel />}
      {tab === "accounts" && <AccountsPanel />}
      {tab === "scope" && <ScopePanel />}
      {tab === "rules" && <RulesPanel />}
      {tab === "jobs" && <JobsPanel />}
      {tab === "status" && <StatusPanel />}

      <LinkAction to="/" icon={House}>
        ← {t("common.back")}
      </LinkAction>
    </main>
  );
}
